import React, { useEffect, useState } from 'react';
import TemplateCard from '../../common/TemplateCard';
import EditTemplateModal from '../../common/EditTemplateModal';
import { getTemplates, getSubjectBody, updateSubjectBody, getSignOff, updateSignOff } from '../../../services/template.service';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';

interface Template {
  code: string;
  description: string;
  knownParameters: string[];
}

const Templates = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [signOff, setSignOff] = useState('');

  const hasFetched = React.useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    const fetchTemplates = async () => {
      try {
        const data = (await getTemplates()) as { data: unknown };
        setTemplates(Array.isArray(data.data) ? data.data : []);
      } catch (error) {
        // handle error (show message, etc.)
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [modalError, setModalError] = useState<string | undefined>(undefined);
  const [modalLoading, setModalLoading] = useState(false);

  const { notifyMessage } = useNotifyMessage();

  const handleEdit = async (code: string) => {
    setModalLoading(true);
    setModalError(undefined);
    setSelectedCode(code);
    try {
      const [templateResult, signOffResult]: any = await Promise.all([getSubjectBody(code), getSignOff()]);
      setSubject(templateResult.data.subject || '');
      setBody(templateResult.data.body || '');
      setSignOff(signOffResult.data.contents || '');
      setModalOpen(true);
    } catch (err: any) {
      setModalError('Failed to fetch template details.');
      setSubject('');
      setBody('');
      setModalOpen(true);
    } finally {
      setModalLoading(false);
    }
  };
  const handleUpdate = async (newSubject: string, newBody: string, newSignOff: string) => {
    try {
      await Promise.all([updateSubjectBody(selectedCode!, newSubject, newBody), updateSignOff(newSignOff)]);
      setSubject(newSubject);
      setBody(newBody);
      setSignOff(newSignOff);
      setModalOpen(false);
      notifyMessage('Template updated successfully.', 'success');
    } catch (err: any) {
      notifyMessage(err?.message || 'Cannot update parameters.', 'error');
    }
  };
  return (
    <div>
      <h1>Communication Templates</h1>
      {loading ? (
        <p>Loading templates...</p>
      ) : (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            marginTop: '24px',
          }}
        >
          {Array.isArray(templates) && templates.length > 0 ? (
            templates.map((tpl: any) => (
              <TemplateCard
                key={tpl.code}
                code={tpl.code}
                description={tpl.description}
                knownParameters={tpl.knownParameters}
                onEdit={() => handleEdit(tpl.code)}
              />
            ))
          ) : (
            <p>No templates found.</p>
          )}
        </div>
      )}

      <EditTemplateModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        code={selectedCode || ''}
        subject={subject}
        body={body}
        signOff={signOff}
        onUpdate={handleUpdate}
        error={modalError}
      />
    </div>
  );
};

export default Templates;
